import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, FormControlLabel, IconButton, Link, Paper, Stack, Switch, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useSnackbar } from 'notistack';
import { api } from '../../services/api';
import { auth } from '../../services/auth';
import OptimizeScheduleDialog from './OptimizeScheduleDialog';
import IperfScheduleDialog from './IperfScheduleDialog';
import NeighborScanScheduleDialog from './NeighborScanScheduleDialog';
import { describeIperfCapScope, describeIperfRequest } from '../../utils/iperfScheduleUtils';
import { describeNeighborScanRequest, describeNeighborScanSelection } from '../../utils/neighborScanScheduleUtils';
import { openNeighborScanTracker } from '../../utils/neighborScanTracking';
import {
  SCHEDULE_STATUS_META,
  SCHEDULE_STEP_OPTIONS,
  describeOptimizeScope,
  describeRepeat,
  describeRunPhase,
  formatInTimeZone,
} from '../../utils/optimizeScheduleUtils';

const POLL_ACTIVE_MS = 5000;
const POLL_IDLE_MS = 30000;

const stepLabel = (step) => SCHEDULE_STEP_OPTIONS.find((o) => o.value === step)?.label.split(' (')[0] || step;

// Active = will run again (enabled with a next run) or is running now.
const isActiveSchedule = (s, activeRun) => (s.enabled && Boolean(s.next_run_at)) || activeRun?.schedule_id === s.id;

const KIND_LABEL = { optimize: 'scheduled optimize', iperf: 'scheduled iperf test', neighbor_scan: 'scheduled neighbours scan' };

// Per-kind API calls, titles and texts.
const KINDS = {
  optimize: {
    title: 'Optimize Schedule',
    list: () => api.getOptimizeSchedules(),
    remove: (id) => api.deleteOptimizeSchedule(id),
    kept: 'Plans it already applied stay in the Plans tab.',
    column: 'Applies',
    scope: (s) => describeOptimizeScope(s.optimize_request),
  },
  iperf: {
    title: 'Iperf Schedule',
    list: () => api.getIperfSchedules(),
    remove: (id) => api.deleteIperfSchedule(id),
    kept: 'Test runs it already started stay in the Iperf tab.',
    column: 'Measures',
    scope: (s) => describeIperfCapScope(s.iperf_request),
  },
  neighbor_scan: {
    title: 'Neighbours Scan Schedule',
    list: () => api.getNeighborScanSchedules(),
    remove: (id) => api.deleteNeighborScanSchedule(id),
    kept: 'Scans it already started stay in Neighbours → Recent Scans.',
    column: 'Scans',
    scope: (s) => describeNeighborScanSelection(s.neighbor_scan_request),
  },
};

/**
 * RRM → Optimize → Optimize Schedule / Iperf Schedule: the site's scheduled
 * runs of one kind. Shows what runs when (site time), the run in progress
 * (of either kind: the site runs one scheduled job at a time), why runs
 * are on hold, and each schedule's last result. Optimize schedules carry
 * their own optimize scope and parameters; iperf schedules carry a regular
 * iperf test's CAPs and settings.
 * The backend serves this from memory, so polling costs no database reads.
 *
 * @param {'optimize'|'iperf'|'neighbor_scan'} [props.kind]
 */
export default function OptimizeSchedulePanel({
  kind = 'optimize', isActive = true, devices, onOpenPlan, onOpenIperfRun, onGoToOptimize, getScheduleSettings,
}) {
  const isIperf = kind === 'iperf';
  const isScan = kind === 'neighbor_scan';
  const cfg = KINDS[kind] || KINDS.optimize;
  const { enqueueSnackbar } = useSnackbar();
  const canWrite = auth.canWriteBackhaul();
  // Dialog state: null = closed, { schedule: null } = new, { schedule } = edit.
  const [editor, setEditor] = useState(null);
  const [data, setData] = useState({ schedules: [], active_run: null, busy_reason: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const activeRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const next = await cfg.list();
      activeRef.current = Boolean(next.active_run);
      setData(next);
      setError('');
    } catch (err) {
      setError(err?.message || 'Failed to load scheduled runs');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);

  // Poll while the page is shown; faster while a run is in progress. Keeps
  // polling after a failed request, so the page never freezes on stale state.
  useEffect(() => {
    if (!isActive) return undefined;
    let cancelled = false;
    let timer = null;
    const tick = async () => {
      await load();
      if (!cancelled) timer = setTimeout(tick, activeRef.current ? POLL_ACTIVE_MS : POLL_IDLE_MS);
    };
    tick();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [isActive, load]);

  const remove = async (s) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete scheduled run "${s.name}"? ${cfg.kept}`)) return;
    setDeletingId(s.id);
    try {
      await cfg.remove(s.id);
      enqueueSnackbar(`"${s.name}" deleted.`, { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(err?.data?.error || err?.message || 'Failed to delete', { variant: err?.statusCode === 409 ? 'warning' : 'error' });
    } finally {
      setDeletingId('');
      load();
    }
  };

  const openPlan = (planId) => onOpenPlan?.(planId);

  const { schedules, active_run: activeRun, busy_reason: busyReason } = data;
  const active = schedules.filter((s) => isActiveSchedule(s, activeRun));
  const inactive = schedules.filter((s) => !isActiveSchedule(s, activeRun));
  const shown = showInactive ? [...active, ...inactive] : active;
  const activeTz = schedules.find((s) => s.id === activeRun?.schedule_id)?.timezone;

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="h6" sx={{ flex: 1 }}>{cfg.title}</Typography>
        {canWrite && (
          <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => setEditor({ schedule: null })}>
            New schedule
          </Button>
        )}
        <Tooltip title="Refresh">
          <span><IconButton size="small" onClick={load} disabled={loading}><RefreshIcon fontSize="small" /></IconButton></span>
        </Tooltip>
      </Stack>
      {isScan && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Each scheduled run starts a neighbours scan, configured like Run Scan in the Neighbours tab (which devices,
          seconds per channel, channels, devices scanning at once), at a set site time. Each run appears in
          Neighbours → Recent Scans with its tracker. A scan is light on the devices, so one in progress does not hold
          other scheduled runs; only a second scan waits for it.
        </Typography>
      )}
      {isIperf && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Each scheduled run starts an iperf test, configured like Start Test in the Iperf tab, at a set site time. Its
          runs appear in the Iperf tab, and Optimize uses their hop (child → parent) measurements. The site runs one
          scheduled job at a time: give the test time to finish before a scheduled optimize.
        </Typography>
      )}
      {!isIperf && !isScan && (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Each scheduled run optimizes the mesh with its own scope and parameters and applies the plan at a set site
        time. A schedule starts from the defaults; its dialog can also copy the
        {' '}<Link component="button" variant="body2" onClick={() => onGoToOptimize?.()}>Optimize</Link> tab&apos;s current
        parameters. Applied plans also appear in the Plans tab.
      </Typography>
      )}

      {activeRun && (
        <Alert severity="info" icon={<CircularProgress size={18} />} sx={{ mb: 1.5 }}>
          <strong>{activeRun.name}</strong>{activeRun.kind && activeRun.kind !== kind ? ` (${KIND_LABEL[activeRun.kind] || activeRun.kind})` : ''} is running now: {describeRunPhase(activeRun.phase)}.
          {' '}Started {formatInTimeZone(activeRun.started_at, activeTz)}. Other scheduled runs are skipped until it finishes.
        </Alert>
      )}
      {!activeRun && busyReason && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          Scheduled runs are on hold: {busyReason}. A run that comes due now is skipped and shown as skipped below.
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error} (retrying automatically)</Alert>}

      {inactive.length > 0 && (
        <FormControlLabel
          sx={{ mb: 1 }}
          control={<Switch size="small" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />}
          label={`Show finished and paused (${inactive.length})`}
        />
      )}

      {loading && schedules.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
      ) : shown.length === 0 ? (
        <Alert severity="info">No active scheduled runs{canWrite ? '. Click New schedule to add one.' : '.'}</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Scheduled run</TableCell>
                <TableCell>Next run (site time)</TableCell>
                <TableCell>{cfg.column}</TableCell>
                <TableCell>Last run</TableCell>
                {canWrite && <TableCell align="right" />}
              </TableRow>
            </TableHead>
            <TableBody>
              {shown.map((s) => {
                const isRunning = activeRun?.schedule_id === s.id;
                const status = isRunning ? 'running' : s.last_status;
                const meta = SCHEDULE_STATUS_META[status];
                const message = isRunning
                  ? `${describeRunPhase(activeRun.phase).replace(/^./, (c) => c.toUpperCase())}…`
                  : s.last_message;
                return (
                  <TableRow key={s.id} hover>
                    <TableCell sx={{ minWidth: 220 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{s.name}</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {describeRepeat(s.repeat, s.start_local)} · {s.timezone}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Scope: {cfg.scope(s)}{s.created_by ? ` · by ${s.created_by}` : ''}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {!s.enabled ? <Chip size="small" label="Paused" />
                        : s.next_run_at ? formatInTimeZone(s.next_run_at, s.timezone)
                          : <Chip size="small" label="Finished" />}
                    </TableCell>
                    <TableCell>
                      {isScan && <Typography variant="caption">{describeNeighborScanRequest(s.neighbor_scan_request)}</Typography>}
                      {isIperf && <Typography variant="caption">{describeIperfRequest(s.iperf_request)}</Typography>}
                      {!isIperf && !isScan && (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {s.pre_iperf && (
                            <Tooltip title={`Runs this iperf test first and optimizes once it has finished: ${describeIperfCapScope(s.iperf_request)} · ${describeIperfRequest(s.iperf_request)}`}>
                              <Chip size="small" color="info" variant="outlined" label="Iperf first" />
                            </Tooltip>
                          )}
                          {(s.steps || []).map((st) => <Chip key={st} size="small" variant="outlined" label={stepLabel(st)} />)}
                        </Stack>
                      )}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 340 }}>
                      {meta ? (
                        <>
                          <Stack direction="row" spacing={0.75} alignItems="center">
                            <Chip
                              size="small"
                              color={meta.color}
                              label={meta.label}
                              icon={isRunning ? <CircularProgress size={12} color="inherit" /> : undefined}
                            />
                            {s.last_run_at && !isRunning && (
                              <Typography variant="caption" color="text.secondary">{formatInTimeZone(s.last_run_at, s.timezone)}</Typography>
                            )}
                          </Stack>
                          {message && <Typography variant="caption" display="block" sx={{ mt: 0.25 }}>{message}</Typography>}
                          {s.last_plan_id && !isRunning && (
                            <Link component="button" variant="caption" onClick={() => openPlan(s.last_plan_id)}>Open plan</Link>
                          )}
                          {!isRunning && s.last_scan_id && (
                            <Link component="button" variant="caption" onClick={() => openNeighborScanTracker(s.last_scan_id)}>
                              Open scan tracker
                            </Link>
                          )}
                          {!isRunning && (s.last_iperf_run_ids || []).length > 0 && onOpenIperfRun && (
                            <Link component="button" variant="caption" onClick={() => onOpenIperfRun(s.last_iperf_run_ids[0])}>
                              Open in Iperf tab{s.last_iperf_run_ids.length > 1 ? ` (${s.last_iperf_run_ids.length} runs)` : ''}
                            </Link>
                          )}
                        </>
                      ) : <Typography variant="caption" color="text.secondary">Not run yet</Typography>}
                    </TableCell>
                    {canWrite && (
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title={isRunning ? 'Running now; edit it after the run finishes' : 'Edit schedule and parameters'}>
                          <span>
                            <IconButton size="small" disabled={isRunning} onClick={() => setEditor({ schedule: s })}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={isRunning ? 'Running now; delete it after the run finishes' : 'Delete'}>
                          <span>
                            <IconButton size="small" disabled={isRunning || deletingId === s.id} onClick={() => remove(s)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {isScan && (
        <NeighborScanScheduleDialog
          open={Boolean(editor)}
          schedule={editor?.schedule || null}
          devices={devices}
          onClose={() => setEditor(null)}
          onSaved={() => {
            setEditor(null);
            load();
          }}
        />
      )}
      {isIperf && (
        <IperfScheduleDialog
          open={Boolean(editor)}
          schedule={editor?.schedule || null}
          devices={devices}
          onClose={() => setEditor(null)}
          onSaved={() => {
            setEditor(null);
            load();
          }}
        />
      )}
      {!isIperf && !isScan && (
        <OptimizeScheduleDialog
          open={Boolean(editor)}
          schedule={editor?.schedule || null}
          devices={devices}
          copyFromOptimize={getScheduleSettings}
          onClose={() => setEditor(null)}
          onSaved={() => {
            setEditor(null);
            load();
          }}
        />
      )}
    </Box>
  );
}
